import Chip from '@/components/ui/Chip';
import { iniciaisDe } from '../montagem';
import { ddmm } from '../frequencia';
import type { FormacaoFrequencia, PessoaFrequencia } from '../types';

interface Props {
  pessoa: PessoaFrequencia;
  fotoUrl: string | null;
  selecionavel: boolean;
  selecionado: boolean;
  aoAbrir: () => void;
  aoAlternar: () => void;
}

type TipoSelo = 'success' | 'warning' | 'danger' | 'neutro';

// Bolinha de uma formação, com a cor da situação.
function Ponto({ formacao }: { formacao: FormacaoFrequencia }) {
  const s = formacao.situacao;
  if (s === 'presente') {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-success-500" />;
  }
  if (s === 'justificada') {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-info-500" />;
  }
  if (s === 'futura') {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-secondary-300" />;
  }
  // falta · aguardando · recusada
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      <span className="h-2.5 w-2.5 rounded-full border-2 border-danger-500 bg-white" />
      {s === 'aguardando' && <i className="ri-time-line text-[10px] text-danger-500" />}
    </span>
  );
}

function seloDe(p: PessoaFrequencia): { rotulo: string; tipo: TipoSelo } | null {
  if (p.fora_da_montagem) return { rotulo: 'Fora', tipo: 'neutro' };
  if (p.grupo === 'nenhuma') return { rotulo: 'Não veio', tipo: 'danger' };
  if (p.faltas > 0 && p.presentes > 0) return { rotulo: `Faltou ${p.faltas}`, tipo: 'warning' };
  if (p.em_dia) return { rotulo: 'Em dia', tipo: 'success' };
  return null;
}

export default function LinhaFrequencia({
  pessoa,
  fotoUrl,
  selecionavel,
  selecionado,
  aoAbrir,
  aoAlternar,
}: Props) {
  const selo = seloDe(pessoa);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={selecionavel ? aoAlternar : aoAbrir}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (selecionavel) aoAlternar();
          else aoAbrir();
        }
      }}
      className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-background-50"
    >
      {selecionavel && (
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
            selecionado
              ? 'border-primary-500 bg-primary-500 text-white'
              : 'border-secondary-300 bg-white'
          }`}
        >
          {selecionado && <i className="ri-check-line text-sm" />}
        </span>
      )}

      {fotoUrl ? (
        <img
          src={fotoUrl}
          alt={pessoa.nome}
          loading="lazy"
          className="h-11 w-11 shrink-0 rounded-full border border-secondary-200 object-cover"
        />
      ) : (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-semibold text-secondary-600">
          {iniciaisDe(pessoa.nome)}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold uppercase text-foreground-950">
          {pessoa.nome}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
          {pessoa.formacoes.map((f) => (
            <span
              key={f.encontro_id}
              className="inline-flex items-center gap-1 text-[11px] text-secondary-500"
            >
              <Ponto formacao={f} />
              {ddmm(f.quando)}
            </span>
          ))}
          {pessoa.formacoes.length === 0 && (
            <span className="text-[11px] text-secondary-400">sem formações</span>
          )}
        </div>
      </div>

      {selo && (
        <Chip tipo={selo.tipo} className="shrink-0">
          {selo.rotulo}
        </Chip>
      )}
    </div>
  );
}